import type { GetCachedTicker } from '@/service/ws/ticker-channel-cache'
import {
  type IncrementalTicker,
  type IncrementalTickerList,
  MarketDataType,
  UpdateType,
} from '@/types/generated/common/types'
import type { MessageSubject } from '@/types/generated/public_api'

export type GetCachedIncrementalTicker = (
  exchange: unknown,
  symbol: string
) => IncrementalTicker | undefined

const defined = <T extends object>(value: T) =>
  Object.fromEntries(Object.entries(value).filter(([, field]) => field !== undefined))

/** Complete state for active incremental channels, including nested partial updates. */
export function createIncrementalTickerCache() {
  const entries = new Map<string, IncrementalTicker>()
  const key = (exchange: unknown, symbol: string) => `${exchange}:${symbol}`
  return {
    record(list: IncrementalTickerList | undefined) {
      for (const ticker of list?.incrementalTickers ?? []) {
        const id = key(ticker.exchange, ticker.symbol)
        const previous = entries.get(id)
        if (!previous || ticker.type === UpdateType.UPDATE_TYPE_SNAPSHOT) {
          entries.set(id, ticker)
          continue
        }
        entries.set(id, {
          ...previous,
          ...defined(ticker),
          greeks: ticker.greeks
            ? { ...previous.greeks, ...defined(ticker.greeks) }
            : previous.greeks,
          stats: ticker.stats ? { ...previous.stats, ...defined(ticker.stats) } : previous.stats,
        })
      }
    },
    get(exchange: unknown, symbol: string) {
      return entries.get(key(exchange, symbol))
    },
    forget(channel: Partial<MessageSubject>) {
      const payload = channel.marketDataStream
      if (payload?.dataType === MarketDataType.MARKET_DATA_TYPE_INCREMENTAL_TICKER) {
        entries.delete(key(payload.exchange, payload.symbol))
      }
    },
    clear() {
      entries.clear()
    },
  }
}

export function replayCachedIncrementalTickers(
  _getCachedTicker: GetCachedTicker,
  payloads: readonly { exchange: unknown; symbol: string; dataType: MarketDataType }[],
  getCachedIncrementalTicker: GetCachedIncrementalTicker
): IncrementalTickerList | undefined {
  const incrementalTickers = payloads.flatMap((payload) => {
    if (payload.dataType !== MarketDataType.MARKET_DATA_TYPE_INCREMENTAL_TICKER) return []
    const ticker = getCachedIncrementalTicker(payload.exchange, payload.symbol)
    return ticker ? [ticker] : []
  })
  return incrementalTickers.length ? { incrementalTickers } : undefined
}
